import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquare, MapPin, Mail } from "lucide-react";
import { PageHero } from "./Services";
import { SITE } from "../lib/site";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll reply within business hours.");
      setDone(true);
    } catch (err) {
      toast.error("Message could not be sent. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={
          <>
            Call. Text. <span className="italic text-[#C5A059]">Walk in.</span>
          </>
        }
        image={SITE.images.desk}
      />

      <section className="py-24 lg:py-32 bg-[#FBF9F5]" data-testid="contact-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="label-mini">Reach out</div>
            <h2 className="font-serif mt-6 text-4xl md:text-5xl leading-[1.05] text-[#0A192F]">
              We're at the counter, all day, every day but Sunday morning.
            </h2>

            <div className="mt-12 space-y-6 text-[#0A192F]">
              <ContactRow icon={<Phone className="w-4 h-4" />} label="Call" href={SITE.phoneHref} value={SITE.phone} testid="contact-phone" />
              <ContactRow icon={<MessageSquare className="w-4 h-4" />} label="Text" href={SITE.smsHref} value={SITE.phone} testid="contact-sms" />
              <ContactRow icon={<Mail className="w-4 h-4" />} label="Email" href={`mailto:${SITE.email}`} value={SITE.email} testid="contact-email" />
              <ContactRow icon={<MapPin className="w-4 h-4" />} label="Visit" href={SITE.mapsUrl} value={SITE.address} testid="contact-map" external />
            </div>

            <div className="mt-12">
              <div className="label-mini">Hours</div>
              <ul className="mt-4 space-y-2 font-mono-tab text-[#0A192F]/85">
                {SITE.hours.map((h) => (
                  <li key={h.day} className="flex justify-between max-w-sm">
                    <span>{h.day}</span>
                    <span className="text-[#C5A059]">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <div className="border border-[#0A192F]/12 bg-white p-10" data-testid="contact-success">
                <h3 className="font-serif text-3xl text-[#0A192F]">Message received.</h3>
                <p className="mt-4 text-[#0A192F]/70">
                  We'll get back to {form.email} within business hours. For urgent
                  requests, please call {SITE.phone}.
                </p>
              </div>
            ) : (
              <Reveal>
                <form onSubmit={submit} className="border border-[#0A192F]/12 bg-white p-8 md:p-10" data-testid="contact-form">
                  <div className="label-mini">Send a message</div>
                  <h3 className="font-serif text-3xl mt-3 text-[#0A192F]">Write to us.</h3>

                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    <Field label="Your Name" value={form.name} onChange={update("name")} testid="c-name" />
                    <Field label="Phone" value={form.phone} onChange={update("phone")} testid="c-phone" type="tel" />
                    <Field label="Email" value={form.email} onChange={update("email")} testid="c-email" type="email" className="md:col-span-2" />
                    <div className="md:col-span-2">
                      <div className="label-mini">Subject</div>
                      <select value={form.subject} onChange={update("subject")} className="input-line" data-testid="c-subject">
                        <option>General inquiry</option>
                        <option>Title transfer</option>
                        <option>Registration / Tags</option>
                        <option>Notary appointment</option>
                        <option>Auto insurance</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-mini">Message</div>
                      <textarea
                        rows={4}
                        value={form.message}
                        onChange={update("message")}
                        className="input-line resize-none"
                        placeholder="Tell us what you need help with…"
                        data-testid="c-message"
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" disabled={loading} className="btn-gold mt-10 w-full justify-center" data-testid="contact-submit">
                    {loading ? "Sending…" : "Send Message"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>

        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-20">
          <div className="rounded-sm overflow-hidden aspect-[16/7] border border-[#0A192F]/10" data-testid="contact-map-embed">
            <iframe
              title="Map to Rhawn Auto Tags"
              src={SITE.mapsEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
              loading="lazy"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </>
  );
}

function ContactRow({ icon, label, value, href, testid, external }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
      className="flex items-start gap-4 group"
    >
      <span className="w-9 h-9 rounded-full border border-[#0A192F]/20 flex items-center justify-center text-[#C5A059] group-hover:border-[#C5A059] transition-colors">
        {icon}
      </span>
      <div>
        <div className="label-mini">{label}</div>
        <div className="mt-1 text-lg text-[#0A192F] link-under inline-block">{value}</div>
      </div>
    </a>
  );
}

function Field({ label, value, onChange, testid, type = "text", className = "" }) {
  return (
    <div className={className}>
      <div className="label-mini">{label}</div>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="input-line"
        data-testid={testid}
        required={["Your Name", "Email"].includes(label)}
      />
    </div>
  );
}
